import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { Textarea } from "../ui/textarea";
import {
  getClausesCategoryTypeList,
  GetClauseTypeList,
} from "@/redux/Masters/clause/actions";
import { Save, X } from "lucide-react";
import CelebrationButton from "../ui/CelebrationButton/CelebrationButton";

const ClauseModal = ({
  open,
  onClose,
  formData,
  errors,
  handleInputChange,
  onSave,
}) => {
  const dispatch = useDispatch();

  // ✅ DATA from reducer
  const { clausesCategoryTypeList = [], clauseTypeList = [] } = useSelector(
    (state) => state.clause,
  );
  const { isAnimate } = useSelector((state) => state.toast);

  useEffect(() => {
    if(!open) return
    dispatch(getClausesCategoryTypeList());
    dispatch(GetClauseTypeList());
  }, [open, dispatch]);

  if (!open) return null;

  const inputClass =
    "w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all";
  return (
    <AnimatePresence>
      {
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            data-testid="location-modal"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {" Add / Modify Clause Info"}
              </h2>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
              {/* Category Type */}
              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-3 text-xs font-medium">
                  Category Type<span className="text-red-500">*</span>
                </label>
                <div className="col-span-9">
                  <select
                    className={inputClass}
                    name="clausesCategoryTypeId"
                    value={formData?.clausesCategoryTypeId}
                    onChange={handleInputChange}
                  >
                    {/* <option>SELECT Commodity Group</option> */}
                    <option>select</option>
                    {clausesCategoryTypeList?.map((group, key) => (
                      <option key={key} value={group.clauseCategoryTypeId}>
                        {group.clauseCategoryType}
                      </option>
                    ))}
                  </select>
                  {errors.clausesCategoryTypeId && (
                    <p className="text-red-500 text-xs">
                      {errors.clausesCategoryTypeId}
                    </p>
                  )}
                </div>
              </div>

              {/* Country ISO Code */}
              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-3 text-xs font-medium">
                  Clause Type <span className="text-red-500">*</span>
                </label>
                <div className="col-span-9">
                  <select
                    className={inputClass}
                    name="clauseTypeId"
                    value={formData?.clauseTypeId}
                    onChange={handleInputChange}
                  >
                    {/* <option>SELECT Commodity Group</option> */}
                    <option>select</option>
                    {clauseTypeList?.map((group, key) => (
                      <option key={key} value={group.clauseTypeId}>
                        {group.clauseType}
                      </option>
                    ))}
                  </select>
                  {errors.clauseTypeId && (
                    <p className="text-red-500 text-xs">
                      {errors.clauseTypeId}
                    </p>
                  )}
                </div>
              </div>

              {/* Clause Description */}
              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-3 text-xs font-medium">
                  Clause Description <span className="text-red-500">*</span>
                </label>
                <div className="col-span-9">
                  <Textarea
                    name="clauseDescription"
                    value={formData.clauseDescription}
                    onChange={handleInputChange}
                    className="w-full h-48 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
            rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500
            focus:border-transparent transition-all"
                  />
                  {errors.clauseDescription && (
                    <p className="text-red-500 text-xs">
                      {errors.clauseDescription}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <CelebrationButton
                onClick={() => onSave()}
                label={"Save"}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                icon={<Save className="w-4 h-4" />}
                isSuccess={isAnimate}
              />
            </div>
          </motion.div>
        </div>
      }
    </AnimatePresence>
  );
};

export default ClauseModal;
